import React, { useMemo, useState, useEffect } from 'react'
import { Spinner, Table, Form, Row, Col } from 'react-bootstrap'
import styled from 'styled-components'
import { Content } from '../../../types/models/content'
import { useAppSelector, useAppDispatch, useAnnotationStats } from '../../../store/hooks'
import {
  selectSelectedProjectId,
  selectActiveTab,
  selectFilterTags,
  clearFilterTags,
  setActiveTab,
} from '../../../store/slices/projectSlice'
import type { ActiveTab } from '../../../store/slices/projectSlice'
import {
  selectAnnotatedPage,
  selectUnannotatedPage,
  setAnnotatedPage,
  setUnannotatedPage,
} from '../../../store/slices/annotationSlice'
import {
  contentApi,
  useDeleteContentMutation,
  useFetchAnnotatedContentQuery,
} from '../../../services/contentApi'
import { selectDatasetSources } from '../../../store/slices/datasetSlice'
import ImageGalleryHeader from '../subviews/ImageGalleryHeader'
import SelectedImagesStats from '../../cards/SelectedImagesStats'
import ConfirmDialog from '../../modals/ConfirmDialog'
import ErrorDialog from '../../modals/ErrorDialog'
import { FaSort } from 'react-icons/fa'
import PillsDataset from '../../PillsDataset'
import PillsClassification from '../../PillsClassification'
import PillsBBox from '../../PillsBBox'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { apiTags } from '../../../services/apiTags'
import { annotationsApi } from '../../../services/annotationApi'
import { Annotation } from '../../../types/models/annotation'

// Styled Components
const MainContentContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  height: 75vh;
`

const ImageGridContainer = styled.div`
  flex: 1;
  overflow: auto;
  background: transparent;
  border-radius: 12px;
  padding: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.3);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: rgba(102, 126, 234, 0.5);
  }
`

const StyledTable = styled(Table)`
  color: #e0e0e0;
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;

  th {
    background: transparent;
    border: none;
    padding: 1rem 1.25rem;
    font-weight: 600;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    transition: color 0.3s ease;
    white-space: nowrap;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    &:hover {
      color: #fff;
    }

    i {
      margin-left: 0.5rem;
      font-size: 0.8rem;
    }

    .sort-icon {
      margin-left: 0.5rem;
      font-size: 0.8rem;
      opacity: 0.7;
    }
  }

  td {
    background: transparent;
    padding: 1rem 1.25rem;
    border: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    vertical-align: middle;
    font-size: 0.95rem;
    color: #e0e0e0;

    .tag-name {
      font-weight: 500;
      color: #fff;
    }

    .color-preview {
      display: inline-block;
      width: 20px;
      height: 20px;
      border-radius: 4px;
      margin-right: 0.75rem;
      border: 2px solid rgba(255, 255, 255, 0.2);
      vertical-align: middle;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    .hex-color {
      color: rgba(255, 255, 255, 0.6);
      font-family: monospace;
      font-size: 0.9rem;
    }

    .badge {
      font-weight: 500;
      font-size: 0.85rem;
      padding: 0.4em 0.8em;
      background: rgba(102, 126, 234, 0.2);
      color: #e0e0e0;
      border: 1px solid rgba(102, 126, 234, 0.3);
      border-radius: 6px;
    }

    .date {
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.9rem;
    }

    .form-check {
      margin: 0;

      .form-check-input {
        background-color: transparent;
        border-color: rgba(102, 126, 234, 0.3);
        cursor: pointer;

        &:checked {
          background-color: #667eea;
          border-color: #667eea;
        }

        &:focus {
          box-shadow: 0 0 0 0.25rem rgba(102, 126, 234, 0.25);
        }
      }
    }
  }

  tbody tr {
    background: transparent;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: rgba(102, 126, 234, 0.1);
      td {
        background-color: transparent;
      }
    }

    &.selected {
      background-color: rgba(102, 126, 234, 0.15);
      td {
        background-color: transparent;
      }
    }
  }

  .tiny-pills {
    font-size: 0.85rem;

    .class-pills > div,
    .dataset-pills > div {
      padding: 0.25rem 0.75rem;
      height: 1.5rem;
      margin-top: 0;
    }
  }
`

const ThumbnailImage = styled.img`
  width: 128px;
  height: 128px;
  object-fit: contain;
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border-radius: 12px;
  border: 1px solid rgba(102, 126, 234, 0.2);
  padding: 0.25rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`

const StatsPanel = styled.div`
  width: 300px;
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  height: 100%;
  overflow: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  h4 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1.25rem;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    svg {
      opacity: 0.7;
    }
  }

  .section {
    padding: 1.25rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  .stats-label {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  .stats-value {
    color: white;
    font-weight: 500;
    font-size: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .text-muted {
      color: rgba(255, 255, 255, 0.5) !important;
      font-size: 0.9rem;
    }
  }

  .progress {
    height: 6px;
    background: rgba(255, 255, 255, 0.1);
    margin-top: 0.5rem;
    border-radius: 3px;
  }

  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.3);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: rgba(102, 126, 234, 0.5);
  }
`

const ContentManagerContainer = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  padding: 0.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  .table {
    color: #e0e0e0;
    margin-bottom: 0;
    border-collapse: separate;
    border-spacing: 0;

    th {
      background: transparent;
      border: none;
      padding: 1rem 1.25rem;
      font-weight: 600;
      font-size: 0.9rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      transition: color 0.3s ease;
      white-space: nowrap;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);

      &:hover {
        color: #fff;
      }

      i {
        margin-left: 0.5rem;
        font-size: 0.8rem;
      }
    }

    td {
      background: transparent;
      padding: 1rem 1.25rem;
      border: none;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      vertical-align: middle;
      font-size: 0.95rem;
      color: #e0e0e0;

      .tag-name {
        font-weight: 500;
        color: #fff;
      }

      .color-preview {
        display: inline-block;
        width: 20px;
        height: 20px;
        border-radius: 4px;
        margin-right: 0.75rem;
        border: 2px solid rgba(255, 255, 255, 0.2);
        vertical-align: middle;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      }

      .hex-color {
        color: rgba(255, 255, 255, 0.6);
        font-family: monospace;
        font-size: 0.9rem;
      }

      .badge {
        font-weight: 500;
        font-size: 0.85rem;
        padding: 0.4em 0.8em;
        background: rgba(102, 126, 234, 0.2);
        color: #e0e0e0;
        border: 1px solid rgba(102, 126, 234, 0.3);
        border-radius: 6px;
      }

      .date {
        color: rgba(255, 255, 255, 0.6);
        font-size: 0.9rem;
      }

      .btn-outline-danger {
        padding: 0.5rem 1rem;
        min-width: 100px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        border: 1px solid rgba(220, 53, 69, 0.5);
        color: #dc3545;
        background: transparent;
        transition: all 0.3s ease;
        border-radius: 8px;

        &:hover {
          background: rgba(220, 53, 69, 0.15);
          color: #ff4757;
          border-color: #dc3545;
          transform: translateY(-1px);
          box-shadow: 0 4px 15px rgba(220, 53, 69, 0.2);
        }

        i {
          font-size: 0.9rem;
        }
      }
    }

    tbody tr {
      background: transparent;
      transition: background-color 0.3s ease;

      &:hover {
        background-color: rgba(102, 126, 234, 0.1);
        td {
          background-color: transparent;
        }
      }
    }

    .form-check {
      margin: 0;

      .form-check-input {
        background-color: transparent;
        border-color: rgba(102, 126, 234, 0.3);
        cursor: pointer;

        &:checked {
          background-color: #667eea;
          border-color: #667eea;
        }

        &:focus {
          box-shadow: 0 0 0 0.25rem rgba(102, 126, 234, 0.25);
        }
      }
    }
  }

  .header-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem;
    background: transparent;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    .search-box {
      flex: 1;
      max-width: 300px;
      margin-right: 1rem;

      .form-control {
        background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
        border: 1px solid rgba(102, 126, 234, 0.2);
        color: #fff;
        padding: 0.5rem 1rem;
        font-size: 0.95rem;
        border-radius: 8px;
        transition: all 0.3s ease;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        backdrop-filter: blur(10px);

        &:focus {
          border-color: rgba(102, 126, 234, 0.5);
          box-shadow: 0 0 0 0.25rem rgba(102, 126, 234, 0.25), 0 8px 25px rgba(0, 0, 0, 0.3);
          transform: translateY(-1px);
        }

        &::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }
      }
    }

    .btn {
      padding: 0.5rem 1rem;
      font-weight: 500;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      transition: all 0.3s ease;
      border-radius: 8px;

      &:hover {
        transform: translateY(-2px);
      }

      i,
      svg {
        font-size: 0.9rem;
      }
    }
  }

  .bulk-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1.25rem;
    background: rgba(220, 53, 69, 0.15);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    span {
      font-size: 0.95rem;
      font-weight: 500;
    }

    .btn-danger {
      background: linear-gradient(135deg, #dc3545 0%, #bb2d3b 100%);
      border-color: #dc3545;
      color: #fff;
      font-weight: 500;
      padding: 0.4rem 0.75rem;
      border-radius: 8px;
      box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);

      &:hover {
        background: linear-gradient(135deg, #bb2d3b 0%, #a02834 100%);
        color: #fff;
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(220, 53, 69, 0.4);
      }

      i {
        font-size: 0.9rem;
      }
    }
  }
`

interface DatasetManageContentProps {
  itemsPerPage?: number
  showAllDatasets?: boolean // If true, ignore dataset sources filter and show all datasets
  datasetSources?: string[] // Optional prop to override global dataset selection
}

const DatasetManageContent: React.FC<DatasetManageContentProps> = ({
  itemsPerPage = 200,
  showAllDatasets = false,
  datasetSources: propDatasetSources,
}) => {
  // Local state
  const [selectedAnnotatedImages, setSelectedAnnotatedImages] = useState<Content[]>([])
  const [selectedContentImages, setSelectedContentImages] = useState<Content[]>([])
  const [annotatedAnchorIndex, setAnnotatedAnchorIndex] = useState<number | null>(null)
  const [annotatedAnchorSelected, setAnnotatedAnchorSelected] = useState<boolean>(false)
  const [contentAnchorIndex, setContentAnchorIndex] = useState<number | null>(null)
  const [contentAnchorSelected, setContentAnchorSelected] = useState<boolean>(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false)
  const [deleteContent] = useDeleteContentMutation()
  // Per-tab sorting state
  const [sortFieldAnnotated, setSortFieldAnnotated] = useState<string>('updated_at')
  const [sortDirectionAnnotated, setSortDirectionAnnotated] = useState<'asc' | 'desc'>('desc')
  const [sortFieldContent, setSortFieldContent] = useState<string>('updated_at')
  const [sortDirectionContent, setSortDirectionContent] = useState<'asc' | 'desc'>('desc')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)
  const [imagesPerRow, setImagesPerRow] = useState<number>(4)

  // Redux selectors and dispatch
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const activeTab = useAppSelector(selectActiveTab)
  const selectedFilterTags = useAppSelector(selectFilterTags)
  const annotatedPage = useAppSelector(selectAnnotatedPage)
  const unannotatedPage = useAppSelector(selectUnannotatedPage)
  // Use prop dataset sources if provided, otherwise fall back to global state
  const globalDatasetSources = useAppSelector(selectDatasetSources)
  const datasetSources = propDatasetSources || globalDatasetSources

  const { data: annoStats } = useAnnotationStats(
    selectedProjectId || '',
    project?.datasetid || '',
    showAllDatasets ? [] : datasetSources?.includes('All') ? [] : datasetSources || []
  )

  // Current tab sorting
  const currentSortField = activeTab === 'annotated' ? sortFieldAnnotated : sortFieldContent
  const currentSortDirection =
    activeTab === 'annotated' ? sortDirectionAnnotated : sortDirectionContent

  // Map unsupported sort fields for server API per tab
  const apiSortFieldAnnotated =
    sortFieldAnnotated === 'annotation' ? 'updated_at' : sortFieldAnnotated
  const apiSortFieldContent = sortFieldContent === 'annotation' ? 'updated_at' : sortFieldContent

  const annotatedContentArgs = useMemo(
    () => ({
      project_id: project?.id || '',
      dataset_id: project?.datasetid,
      tag_id:
        selectedFilterTags?.length > 0 &&
        !selectedFilterTags.some((tag) => tag.name === 'Unlabeled')
          ? selectedFilterTags.map((t) => t.id)
          : undefined,
      metadata_filter:
        showAllDatasets ||
        (datasetSources && datasetSources.length > 0 && !datasetSources.includes('All'))
          ? showAllDatasets
            ? undefined
            : { datasource: datasetSources }
          : undefined,
      limit: itemsPerPage,
      page: annotatedPage - 1,
      operator: 'and' as const,
      sort_key: apiSortFieldAnnotated,
      sort_val: sortDirectionAnnotated === 'asc' ? 1 : -1,
    }),
    [
      project?.id,
      project?.datasetid,
      selectedFilterTags,
      datasetSources,
      itemsPerPage,
      annotatedPage,
      apiSortFieldAnnotated,
      sortDirectionAnnotated,
      showAllDatasets,
    ]
  )

  const unannotatedContentArgs = useMemo(
    () => ({
      project_id: project?.id || '',
      dataset_id: undefined,
      metadata_filter:
        showAllDatasets ||
        (datasetSources && datasetSources.length > 0 && !datasetSources.includes('All'))
          ? showAllDatasets
            ? undefined
            : { datasource: datasetSources }
          : undefined,
      tag_id:
        selectedFilterTags?.length > 0 &&
        !selectedFilterTags.some((tag) => tag.name === 'Unlabeled')
          ? selectedFilterTags.map((t) => t.id)
          : undefined,
      limit: itemsPerPage,
      page: unannotatedPage - 1,
      operator: 'and' as const,
      sort_key: apiSortFieldContent,
      sort_val: sortDirectionContent === 'asc' ? 1 : -1,
    }),
    [
      project?.id,
      datasetSources,
      selectedFilterTags,
      itemsPerPage,
      unannotatedPage,
      apiSortFieldContent,
      sortDirectionContent,
      showAllDatasets,
    ]
  )

  const annotatedContentQuery = useMemo(
    () => ({
      skip: activeTab !== 'annotated',
    }),
    [activeTab]
  )

  const unannotatedContentQuery = useMemo(
    () => ({
      skip: activeTab !== 'content',
    }),
    [activeTab]
  )

  const { data: annotatedContent } = useFetchAnnotatedContentQuery(annotatedContentArgs, {
    ...annotatedContentQuery,
    refetchOnMountOrArgChange: true,
  })

  const { data: unannotatedContent } = useFetchAnnotatedContentQuery(unannotatedContentArgs, {
    ...unannotatedContentQuery,
    refetchOnMountOrArgChange: true,
  })

  useEffect(() => {
    if (unannotatedContent?.content?.length === 0) {
      dispatch(setActiveTab('annotated'))
    }
  }, [dispatch, unannotatedContent?.content?.length])

  const filteredContent = useMemo(() => {
    const contentToProcess =
      activeTab === 'annotated'
        ? annotatedContent?.content || []
        : unannotatedContent?.content || []

    if (activeTab === 'annotated' && selectedFilterTags?.length > 0) {
      return contentToProcess
        .map((content: Content) => ({
          ...content,
          annotation: content.annotation?.map((anno) => ({
            ...anno,
            tagids: anno.tagids || [],
          })) || [
            {
              id: '',
              contentid: content.id,
              created_at: content.created_at,
              datasetid: project?.datasetid,
              projectid: content.projectid,
              split: '',
              tagids: [],
              updated_at: content.updated_at,
              userid: content.userid,
              metadata: {},
            },
          ],
        }))
        .filter((content: any) => {
          const hasUnlabeledTag = selectedFilterTags.some((tag) => tag.name === 'Unlabeled')
          if (hasUnlabeledTag) {
            return (
              content.annotation &&
              content.annotation.length > 0 &&
              (!content.annotation[0].tagids || content.annotation[0].tagids.length === 0)
            )
          }
          const contentTagIds = content.annotation?.[0]?.tagids || []
          const selectedTagIds = selectedFilterTags.map((t) => t.id)
          return contentTagIds.some((tagId: string) => selectedTagIds.includes(tagId))
        })
    }
    return contentToProcess.map((content: Content) => ({
      ...content,
      annotation: content.annotation?.map((anno: Annotation) => ({
        ...anno,
        tagids: anno.tagids || [],
      })) || [
        {
          id: '',
          contentid: content.id,
          created_at: content.created_at,
          datasetid: undefined,
          projectid: content.projectid,
          split: '',
          tagids: [],
          updated_at: content.updated_at,
          userid: content.userid,
          metadata: {},
        },
      ],
    }))
  }, [
    activeTab,
    annotatedContent?.content,
    unannotatedContent?.content,
    selectedFilterTags,
    project?.datasetid,
  ]) as Content[]

  const handlePageChange = (pageNumber: number) => {
    if (activeTab === 'annotated') {
      const totalAnnotated = annoStats?.statistics?.total_annotated_images || 0
      const maxPage = Math.max(1, Math.ceil(totalAnnotated / itemsPerPage))
      const newPage = Math.max(1, Math.min(pageNumber, maxPage))
      dispatch(setAnnotatedPage(newPage))
    } else {
      const totalUnannotated = annoStats?.statistics?.total_unannotated_images || 0
      const maxPage = Math.max(1, Math.ceil(totalUnannotated / itemsPerPage))
      const newPage = Math.max(1, Math.min(pageNumber, maxPage))
      dispatch(setUnannotatedPage(newPage))
    }
  }

  // Add tab handling functions
  const handleTabSelect = (key: string | null) => {
    if (key === 'content') {
      dispatch(setActiveTab('content' as ActiveTab))
    } else if (key === 'annotated') {
      dispatch(setActiveTab('annotated' as ActiveTab))
    }
  }

  // Selection handlers
  const handleImageClick = (
    e: React.MouseEvent<HTMLTableRowElement | HTMLInputElement>,
    content: Content,
    index: number
  ) => {
    const isAnnotatedTab = activeTab === 'annotated'
    const currentSelection = isAnnotatedTab ? selectedAnnotatedImages : selectedContentImages
    const setCurrentSelection = isAnnotatedTab
      ? setSelectedAnnotatedImages
      : setSelectedContentImages
    const anchorIndex = isAnnotatedTab ? annotatedAnchorIndex : contentAnchorIndex
    const setAnchorIndex = isAnnotatedTab ? setAnnotatedAnchorIndex : setContentAnchorIndex
    const anchorSelected = isAnnotatedTab ? annotatedAnchorSelected : contentAnchorSelected
    const setAnchorSelected = isAnnotatedTab ? setAnnotatedAnchorSelected : setContentAnchorSelected

    // Shift-range selection
    if (e.shiftKey && anchorIndex !== null && sortedContent.length > 0) {
      const start = Math.min(anchorIndex, index)
      const end = Math.max(anchorIndex, index)
      const rangeItems = sortedContent.slice(start, end + 1)

      const selectedIdSet = new Set(currentSelection.map((img) => img.id))
      if (anchorSelected) {
        rangeItems.forEach((item) => selectedIdSet.add(item.id))
      } else {
        rangeItems.forEach((item) => selectedIdSet.delete(item.id))
      }
      // Build new selection list preserving previous selection order where possible
      const idToContent = new Map<string, Content>()
      ;[...currentSelection, ...rangeItems].forEach((c) => idToContent.set(c.id, c))
      const newSelection: Content[] = Array.from(selectedIdSet)
        .map((id) => idToContent.get(id))
        .filter((c): c is Content => Boolean(c))
      setCurrentSelection(newSelection)
      return
    }

    // Single toggle selection
    const isSelected = currentSelection.some((img) => img.id === content.id)
    const newSelection = isSelected
      ? currentSelection.filter((img) => img.id !== content.id)
      : [...currentSelection, content]
    setCurrentSelection(newSelection)
    setAnchorIndex(index)
    setAnchorSelected(!isSelected)
  }

  const handleSelectAll = () => {
    if (activeTab === 'annotated') {
      setSelectedAnnotatedImages(filteredContent || [])
    } else {
      setSelectedContentImages(filteredContent || [])
    }
  }

  const handleDeselectAll = () => {
    if (activeTab === 'annotated') {
      setSelectedAnnotatedImages([])
    } else {
      setSelectedContentImages([])
    }
  }

  const handleInvertSelection = () => {
    if (!filteredContent) return
    if (activeTab === 'annotated') {
      const newSelection = filteredContent.filter(
        (content) => !selectedAnnotatedImages.some((selected) => selected.id === content.id)
      )
      setSelectedAnnotatedImages(newSelection)
    } else {
      const newSelection = filteredContent.filter(
        (content) => !selectedContentImages.some((selected) => selected.id === content.id)
      )
      setSelectedContentImages(newSelection)
    }
  }

  const handleDelete = async () => {
    const currentSelected =
      activeTab === 'annotated' ? selectedAnnotatedImages : selectedContentImages
    if (currentSelected.length > 0) {
      try {
        await deleteContent({
          project_id: selectedProjectId || '',
          dataset_id: project?.datasetid || '',
          content_ids: currentSelected.map((img) => img.id),
        })
        if (activeTab === 'annotated') {
          setSelectedAnnotatedImages([])
        } else {
          setSelectedContentImages([])
        }
        // Clear any active tag filters after deletion
        dispatch(clearFilterTags())
        dispatch(annotationsApi.util.invalidateTags([apiTags.Annotation, apiTags.AnnotationStats]))
        dispatch(contentApi.util.invalidateTags([apiTags.Content]))
      } catch (error) {
        const apiError = error as { data?: { message?: string } } | undefined
        setErrorMessage(apiError?.data?.message || 'Failed to delete content')
        setShowError(true)
      }
    }
    setShowDeleteConfirm(false)
  }

  // Add sort handler
  const handleSort = (field: string) => {
    if (activeTab === 'annotated') {
      if (sortFieldAnnotated === field) {
        setSortDirectionAnnotated(sortDirectionAnnotated === 'asc' ? 'desc' : 'asc')
      } else {
        setSortFieldAnnotated(field)
        setSortDirectionAnnotated('asc')
      }
    } else {
      if (field === 'annotation') return
      if (sortFieldContent === field) {
        setSortDirectionContent(sortDirectionContent === 'asc' ? 'desc' : 'asc')
      } else {
        setSortFieldContent(field)
        setSortDirectionContent('asc')
      }
    }
  }

  // Compute annotation count for sorting
  const getAnnotationCount = React.useCallback(
    (content: Content): number => {
      const first = content.annotation?.[0]
      if (!first) return 0
      if (project?.annotation_type === 'classification') {
        return first.tagids?.length || 0
      }
      const meta = first.metadata || {}
      const bboxCount = Array.isArray(meta.bounding_boxes) ? meta.bounding_boxes.length : 0
      const obbCount = Array.isArray(meta.oriented_bounding_boxes)
        ? meta.oriented_bounding_boxes.length
        : 0
      return bboxCount + obbCount
    },
    [project?.annotation_type]
  )

  // Sort content
  const sortedContent = useMemo(() => {
    if (!filteredContent) return []

    return [...filteredContent].sort((a, b) => {
      let comparison = 0

      switch (currentSortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name)
          break
        case 'size':
          // Sort by total pixels (width * height)
          comparison = a.width * a.height - b.width * b.height
          break
        case 'datasource':
          comparison = (a.metadata?.datasource || '').localeCompare(b.metadata?.datasource || '')
          break
        case 'annotation':
          comparison = getAnnotationCount(a) - getAnnotationCount(b)
          break
        case 'updated_at':
          comparison = new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          break
        default:
          comparison = 0
      }

      return currentSortDirection === 'asc' ? comparison : -comparison
    })
  }, [filteredContent, currentSortField, currentSortDirection, getAnnotationCount])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)

    // Handle invalid dates (like "0001-01-01T00:00:00Z")
    if (date.getFullYear() < 2000) {
      return 'Never'
    }

    const now = new Date()
    // If the date is in the future (data inconsistency), just show the full date
    if (date > now) {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    const diffTime = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return (
        date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
        }) + ' Today'
      )
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return `${diffDays} days ago`
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    }
  }

  return (
    <ContentManagerContainer>
      <Row>
        <Col className="d-flex align-items-center">
          <ImageGalleryHeader
            project={project}
            imagesPerRow={imagesPerRow}
            onImagesPerRowChange={setImagesPerRow}
            showTagFilter={activeTab === 'annotated'}
            currentPage={activeTab === 'annotated' ? annotatedPage : unannotatedPage}
            totalImages={
              activeTab === 'annotated'
                ? annoStats?.statistics.total_annotated_images || 0
                : annoStats?.statistics.total_unannotated_images || 0
            }
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            activeTab={activeTab}
            onTabSelect={handleTabSelect}
            annoStats={annoStats}
          />
        </Col>
      </Row>

      <MainContentContainer>
        <ImageGridContainer>
          {filteredContent?.length === 0 ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : sortedContent?.length > 0 ? (
            <StyledTable hover className="user-select-none">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <Form.Check
                      type="checkbox"
                      checked={
                        (activeTab === 'annotated'
                          ? selectedAnnotatedImages.length
                          : selectedContentImages.length) === sortedContent.length &&
                        sortedContent.length > 0
                      }
                      onChange={(e) => (e.target.checked ? handleSelectAll() : handleDeselectAll())}
                    />
                  </th>
                  <th style={{ width: '128px' }}>Image</th>
                  <th onClick={() => handleSort('size')}>
                    Image Size
                    {currentSortField === 'size' && <FaSort className="sort-icon" />}
                  </th>
                  <th onClick={() => handleSort('datasource')}>
                    Source
                    {currentSortField === 'datasource' && <FaSort className="sort-icon" />}
                  </th>
                  {activeTab === 'annotated' && (
                    <th onClick={() => handleSort('annotation')}>
                      Annotations
                      {currentSortField === 'annotation' && <FaSort className="sort-icon" />}
                    </th>
                  )}
                  <th onClick={() => handleSort('updated_at')}>
                    Last Updated
                    {currentSortField === 'updated_at' && <FaSort className="sort-icon" />}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedContent.map((content: Content, index: number) => (
                  <tr
                    key={content.id}
                    className={
                      (activeTab === 'annotated'
                        ? selectedAnnotatedImages
                        : selectedContentImages
                      ).some((img) => img.id === content.id)
                        ? 'selected'
                        : ''
                    }
                    onClick={(e) => handleImageClick(e, content, index)}
                  >
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={(activeTab === 'annotated'
                          ? selectedAnnotatedImages
                          : selectedContentImages
                        ).some((img) => img.id === content.id)}
                        onChange={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleImageClick(e, content, index)
                        }}
                      />
                    </td>
                    <td>
                      <ThumbnailImage
                        src={content.b64_image ? `data:image/jpeg;base64,${content.b64_image}` : ''}
                        alt={content.name}
                      />
                    </td>
                    <td>{`${content.width}×${content.height}`}</td>
                    <td>
                      {content.metadata?.datasource && (
                        <PillsDataset
                          datasetNames={[content.metadata.datasource]}
                          className="tiny-pills"
                        />
                      )}
                    </td>
                    {activeTab === 'annotated' && (
                      <td>
                        {project?.annotation_type === 'classification' &&
                          content.annotation?.[0] && (
                            <PillsClassification
                              tagIds={content.annotation[0].tagids}
                              className="tiny-pills"
                              maxTags={3}
                            />
                          )}
                        {project?.annotation_type === 'bounding_box' && content.annotation?.[0] && (
                          <PillsBBox
                            bounding_boxes={content.annotation[0]?.metadata?.bounding_boxes || []}
                            showTagCounts={true}
                            maxTags={3}
                          />
                        )}
                      </td>
                    )}
                    <td>{formatDate(content.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <p className="text-muted">No content available.</p>
            </div>
          )}
        </ImageGridContainer>

        <StatsPanel>
          <SelectedImagesStats
            selectedImages={
              activeTab === 'annotated' ? selectedAnnotatedImages : selectedContentImages
            }
            filteredContent={filteredContent}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onInvertSelection={handleInvertSelection}
            setShowDeleteConfirm={setShowDeleteConfirm}
          />
        </StatsPanel>
      </MainContentContainer>

      <ConfirmDialog
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirmRight={handleDelete}
        title="Delete Content"
        message={`Are you sure you want to delete ${
          activeTab === 'annotated' ? selectedAnnotatedImages.length : selectedContentImages.length
        } selected image${
          (activeTab === 'annotated'
            ? selectedAnnotatedImages.length
            : selectedContentImages.length) !== 1
            ? 's'
            : ''
        }? This action cannot be undone.`}
        confirmRightText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ErrorDialog show={showError} onHide={() => setShowError(false)} message={errorMessage} />
    </ContentManagerContainer>
  )
}

export default DatasetManageContent
